import { useNavigate } from "react-router-dom";
import { ArrowLeft, Cookie, Globe, Shield } from "lucide-react";
import { useLang } from "@/i18n";

export default function CookiePolicyPage() {
  const navigate = useNavigate();
  const { t, toggleLang, lang } = useLang();
  const it = lang === "it";

  const openSettings = () => window.dispatchEvent(new Event("openCookieSettings"));

  const Section = ({ title, children }) => (
    <section className="mb-8">
      <h2 className="text-lg font-bold uppercase tracking-wider text-[#2d7a4a] mb-3">{title}</h2>
      <div className="text-[#c0d4c0] text-sm leading-relaxed space-y-3">{children}</div>
    </section>
  );

  return (
    <div className="min-h-screen bg-[#0a1a0f] text-[#e8f0e8]">
      <header className="border-b border-[#1a4a2e] px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/")}
            className="text-[#8faa8f] hover:text-[#2d7a4a] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Cookie className="w-4 h-4 text-[#d4a056]" />
            <span className="font-bold uppercase tracking-wider text-sm text-[#2d7a4a]">Cookie Policy</span>
          </div>
          <button
            data-testid="lang-toggle-cookie"
            onClick={toggleLang}
            className="ml-auto flex items-center gap-1 px-2 py-1 rounded-full glass-card text-xs text-[#d4a056]"
          >
            <Globe className="w-3 h-3" /> {t("langToggle")}
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="cookie-policy-content">
        <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-tight mb-2">Cookie Policy</h1>
        <p className="text-[#8faa8f] text-sm mb-10">
          {it ? "Ultimo aggiornamento: giugno 2026" : "Last updated: June 2026"}
        </p>

        <Section title={it ? "1. Titolare del trattamento" : "1. Data controller"}>
          <p>
            {it
              ? "Il presente sito web è gestito da Spirito Libero Lago Maggiore di Naskide Antonioli (di seguito, il \"Titolare\"). Per qualsiasi richiesta relativa ai cookie o al trattamento dei dati personali puoi contattare il Titolare all'indirizzo email spiritolibero.bynaski@gmail.com o al numero +39 333 793 0166."
              : "This website is operated by Spirito Libero Lago Maggiore of Naskide Antonioli (the \"Controller\"). For any request regarding cookies or the processing of personal data, you can contact the Controller at spiritolibero.bynaski@gmail.com or +39 333 793 0166."}
          </p>
        </Section>

        <Section title={it ? "2. Cosa sono i cookie" : "2. What cookies are"}>
          <p>
            {it
              ? "I cookie sono piccoli file di testo che i siti visitati inviano al dispositivo dell'utente, dove vengono memorizzati per essere ritrasmessi agli stessi siti alla visita successiva. Tecnologie simili (come il local storage del browser) svolgono funzioni analoghe. In questa informativa usiamo il termine \"cookie\" per riferirci a entrambe le tecnologie."
              : "Cookies are small text files that websites send to the user's device, where they are stored and sent back to the same websites on subsequent visits. Similar technologies (such as browser local storage) perform comparable functions. In this notice we use the term \"cookies\" to refer to both."}
          </p>
        </Section>

        <Section title={it ? "3. Cookie utilizzati da questo sito" : "3. Cookies used by this website"}>
          <p className="font-semibold text-[#e8f0e8]">
            {it ? "a) Cookie tecnici (sempre attivi — non richiedono consenso)" : "a) Technical cookies (always active — no consent required)"}
          </p>
          <p>
            {it
              ? "Sono indispensabili per il corretto funzionamento del sito e non richiedono il tuo consenso ai sensi dell'art. 122 del Codice Privacy:"
              : "They are essential for the website to function properly and do not require your consent pursuant to art. 122 of the Italian Privacy Code:"}
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>
              <span className="text-[#e8f0e8] font-mono text-xs">sl_lang</span> —{" "}
              {it ? "memorizza la lingua scelta (italiano/inglese). Durata: persistente." : "stores your chosen language (Italian/English). Duration: persistent."}
            </li>
            <li>
              <span className="text-[#e8f0e8] font-mono text-xs">sl_cookie_consent</span> —{" "}
              {it ? "memorizza le tue scelte sui cookie. Durata: 6 mesi." : "stores your cookie choices. Duration: 6 months."}
            </li>
            <li>
              <span className="text-[#e8f0e8] font-mono text-xs">admin_token</span> —{" "}
              {it ? "utilizzato esclusivamente nell'area riservata di amministrazione. Durata: sessione." : "used exclusively in the reserved admin area. Duration: session."}
            </li>
          </ul>
          <p className="font-semibold text-[#e8f0e8] pt-2">
            {it ? "b) Cookie di terze parti — Stripe (pagamenti)" : "b) Third-party cookies — Stripe (payments)"}
          </p>
          <p>
            {it
              ? "Il pagamento delle prenotazioni avviene sulla piattaforma sicura di Stripe (Stripe Payments Europe, Ltd.). Durante la procedura di pagamento Stripe può installare cookie tecnici e di sicurezza necessari all'erogazione del servizio richiesto (es. prevenzione frodi). Per maggiori informazioni consulta la cookie policy di Stripe: stripe.com/cookies-policy/legal."
              : "Booking payments are processed on Stripe's secure platform (Stripe Payments Europe, Ltd.). During the payment process, Stripe may set technical and security cookies necessary to provide the requested service (e.g. fraud prevention). For more information see Stripe's cookie policy: stripe.com/cookies-policy/legal."}
          </p>
          <p className="font-semibold text-[#e8f0e8] pt-2">
            {it ? "c) Cookie di preferenza e statistici" : "c) Preference and statistics cookies"}
          </p>
          <p>
            {it
              ? "Attualmente questo sito NON utilizza cookie di profilazione, di marketing o di analisi statistica di terze parti. Qualora in futuro venissero introdotti, saranno attivati solo previo tuo consenso espresso tramite il banner, in conformità alle Linee Guida del Garante per la protezione dei dati personali del 10 giugno 2021."
              : "This website currently does NOT use third-party profiling, marketing or analytics cookies. Should they be introduced in the future, they will be activated only with your explicit consent given through the banner, in accordance with the Guidelines of the Italian Data Protection Authority of 10 June 2021."}
          </p>
        </Section>

        <Section title={it ? "4. Come gestire le tue preferenze" : "4. How to manage your preferences"}>
          <p>
            {it
              ? "Puoi modificare o revocare il consenso in qualsiasi momento cliccando sul pulsante qui sotto oppure sul link \"Gestisci Cookie\" presente nel footer del sito. Puoi inoltre eliminare o bloccare i cookie tramite le impostazioni del tuo browser (Chrome, Safari, Firefox, Edge): la disabilitazione dei cookie tecnici potrebbe tuttavia compromettere il funzionamento del sito."
              : "You can change or withdraw your consent at any time by clicking the button below or the \"Manage Cookies\" link in the website footer. You can also delete or block cookies through your browser settings (Chrome, Safari, Firefox, Edge): disabling technical cookies may however affect the functioning of the website."}
          </p>
          <button
            data-testid="cookie-policy-manage-btn"
            onClick={openSettings}
            className="mt-2 bg-[#2d7a4a] text-white font-bold uppercase tracking-wider px-6 py-3 text-sm rounded-lg hover:bg-[#3a9960] transition-all"
          >
            {it ? "Gestisci Cookie" : "Manage Cookies"}
          </button>
        </Section>

        <Section title={it ? "5. Base giuridica e riferimenti normativi" : "5. Legal basis and regulatory references"}>
          <p>
            {it
              ? "Il trattamento tramite cookie tecnici si fonda sul legittimo interesse del Titolare (art. 6.1.f GDPR); l'eventuale uso di cookie non tecnici si fonda sul consenso (art. 6.1.a GDPR). Riferimenti: Regolamento (UE) 2016/679 (GDPR); D.lgs. 196/2003 (Codice Privacy) come modificato dal D.lgs. 101/2018, art. 122; Linee Guida del Garante Privacy sui cookie e altri strumenti di tracciamento del 10 giugno 2021."
              : "Processing through technical cookies is based on the Controller's legitimate interest (art. 6.1.f GDPR); any use of non-technical cookies is based on consent (art. 6.1.a GDPR). References: Regulation (EU) 2016/679 (GDPR); Italian Legislative Decree 196/2003 (Privacy Code) as amended by Legislative Decree 101/2018, art. 122; Guidelines of the Italian Data Protection Authority on cookies and other tracking tools of 10 June 2021."}
          </p>
        </Section>

        <Section title={it ? "6. I tuoi diritti" : "6. Your rights"}>
          <p className="flex items-start gap-2">
            <Shield className="w-4 h-4 text-[#d4a056] flex-shrink-0 mt-0.5" />
            <span>
              {it
                ? "Ai sensi degli artt. 15-22 GDPR hai diritto di accedere ai tuoi dati, chiederne la rettifica o la cancellazione, limitarne od opporti al trattamento e alla portabilità. Puoi inoltre proporre reclamo al Garante per la protezione dei dati personali (www.garanteprivacy.it). Per esercitare i tuoi diritti scrivi a spiritolibero.bynaski@gmail.com."
                : "Pursuant to arts. 15-22 GDPR you have the right to access your data, request its rectification or erasure, restrict or object to its processing, and data portability. You may also lodge a complaint with the Italian Data Protection Authority (www.garanteprivacy.it). To exercise your rights, write to spiritolibero.bynaski@gmail.com."}
            </span>
          </p>
        </Section>

        <button
          data-testid="back-home-btn"
          onClick={() => navigate("/")}
          className="border-2 border-[#2d7a4a] text-[#2d7a4a] font-bold uppercase tracking-wider px-8 py-3 hover:bg-[#2d7a4a] hover:text-white transition-all text-sm rounded-lg"
        >
          <ArrowLeft className="w-4 h-4 inline-block mr-2" />
          {t("backHome")}
        </button>
      </main>
    </div>
  );
}
